import React from 'react';

import {
  ArrayField,
  BooleanField,
  Datagrid,
  TextField,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import {
  Card, CardHeader, Divider, Grid,
} from '@mui/material';

import ReferenceEntityField from '../../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../../customFields/EntityDisplayNameField';
import ColoredScoreField from '../../../customFields/ColoredScoreField';
import RiskClassField from '../../../customFields/RiskClassField';

import Pagination from '../../layout/Pagination';
import NoResults from '../../layout/NoResults';

import { boldDataGridStyle } from '../../../constants/style/datagridStyles';
import { Ping } from '../../../utilities/schemas/ping';
import { EntityTypes } from '../../../constants/entityTypes';
import { TimePeriods } from '../../../constants/timePeriods';

type PingDetails = {
  id: string;
  ip: string;
  count: number;
  lookbackPeriod?: {
    unit: TimePeriods;
    amount: number;
  };
  entities: {
    id: string;
    type: EntityTypes;
    isPingEntity: boolean;
  }[];
};

const SharedIpClusterCard = () => {
  const ping = useRecordContext<Ping>();

  const { data } = useGetOne<PingDetails>(
    `pings/${ping?.id}`,
    { id: 'details' },
    { enabled: !!ping?.id },
  );

  if (!data) return null;

  const {
    ip, count, lookbackPeriod, entities,
  } = data;

  const entityWord = count === 1 ? 'entity' : 'entities';
  const subheader = lookbackPeriod
    ? `${count} ${entityWord} · ${ip} · last ${lookbackPeriod.amount} ${lookbackPeriod.unit}`
    : `${count} ${entityWord} · ${ip}`;

  return (
    <Grid size={12}>
      <Card variant="outlined">
        <CardHeader title="Entities on this IP" subheader={subheader} />
        <Divider />
        <ArrayField source="entities" record={{ entities }} perPage={15}>
          <Datagrid
            sx={boldDataGridStyle}
            empty={<NoResults />}
            bulkActionButtons={false}
          >
            <TextField sortable={false} source="id" label="ID" />
            <ReferenceEntityField source="" label="Display name">
              <EntityDisplayNameField />
            </ReferenceEntityField>
            <TextField sortable={false} source="type" />
            <ReferenceEntityField source="" linkType={false} label="Risk score">
              <ColoredScoreField source="riskScore" />
            </ReferenceEntityField>
            <ReferenceEntityField source="" linkType={false} label="Risk class">
              <RiskClassField source="riskScore" />
            </ReferenceEntityField>
            <BooleanField sortable={false} source="isPingEntity" label="This ping" />
          </Datagrid>
          <Pagination disableEmptyText />
        </ArrayField>
      </Card>
    </Grid>
  );
};

export default SharedIpClusterCard;
